const privatePaths = /^\/(?:order-confirmation|report|assessment|iaq-report|live-room)(?:\/|$)/i;
const privateKeys = /^(?:name|first_?name|last_?name|customer_?name|email|customer_?email|phone|address|access_?code|report_?code|token|auth_?token)$/i;
const email = /[^\s/@]+@[^\s/]+\.[^\s/]+/;

const parseUrl = (value: string): URL | null => {
  try { return new URL(value, typeof window === 'undefined' ? 'https://haveniaq.com' : window.location.href); }
  catch { return null; }
};

export const isPrivateAnalyticsUrl = (value: string): boolean => {
  const url = parseUrl(value);
  if (!url) return true;
  let path = url.pathname;
  try { path = decodeURIComponent(path); } catch { return true; }
  return privatePaths.test(path) || email.test(path) ||
    [...url.searchParams].some(([key, value]) => privateKeys.test(key) || email.test(value));
};

export const analyticsBlocked = (): boolean => typeof window !== 'undefined' &&
  (Boolean(window.__havenAnalyticsBlocked) || isPrivateAnalyticsUrl(window.location.href));

// Analytics needs the destination page, never form values, fragments, or report credentials.
export const sanitizeAnalyticsUrl = (value: string, pathOnly = false): string => {
  if (!value) return '';
  const url = parseUrl(value);
  if (!url || !['http:', 'https:'].includes(url.protocol)) return '';
  let path = url.pathname;
  try { path = decodeURIComponent(path); } catch { return ''; }
  const privateMatch = path.match(/^\/(report|assessment|live-room)(?:\/.*)?$/i);
  if (privateMatch) path = `/${privateMatch[1].toLowerCase()}/:code`;
  else if (email.test(path)) path = '/:redacted';
  // Drop username/password as well as every query parameter and fragment.
  return pathOnly ? path : `${url.origin}${path}`;
};

export const analyticsPageContext = () => ({
  page_location: sanitizeAnalyticsUrl(window.location.href),
  page_path: sanitizeAnalyticsUrl(window.location.href, true),
  page_referrer: sanitizeAnalyticsUrl(document.referrer),
});

export const sanitizeAnalyticsParams = (params?: Record<string, unknown>): Record<string, unknown> | undefined => {
  if (!params) return undefined;
  const clean = (value: unknown, key = ''): unknown => {
    if (privateKeys.test(key)) return undefined;
    if (Array.isArray(value)) return value.map(item => clean(item)).filter(item => item !== undefined);
    if (value && typeof value === 'object') return sanitizeAnalyticsParams(value as Record<string, unknown>);
    if (typeof value !== 'string') return value;
    if (/(?:url|uri|href|referrer|page_location|page_path|destination|link)$/i.test(key) || /^(?:https?:\/\/|\/|mailto:)/i.test(value)) {
      return sanitizeAnalyticsUrl(value, key === 'page_path' || value.startsWith('/'));
    }
    return email.test(value) ? '[redacted]' : value;
  };
  return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, clean(value, key)])
    .filter(([, value]) => value !== undefined));
};
